import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

// Razorpay uses an inline modal, so users normally land on /dashboard after success.
// This page exists as a fallback in case a webhook-driven redirect lands here with ?order_id=.
export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const orderId = params.get("order_id");
    const { refresh } = useAuth();
    const navigate = useNavigate();
    const [status, setStatus] = useState(orderId ? "checking" : "no-order");
    const [details, setDetails] = useState(null);

    useEffect(() => {
        if (!orderId) return;
        let attempts = 0;
        let cancelled = false;
        const poll = async () => {
            if (cancelled) return;
            try {
                const { data } = await api.get(`/payments/transactions/${orderId}`);
                setDetails(data);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    await refresh();
                    return;
                }
                attempts += 1;
                if (attempts >= 8) {
                    setStatus("pending");
                    return;
                }
                setTimeout(poll, 2000);
            } catch {
                attempts += 1;
                if (attempts >= 5) {
                    setStatus("failed");
                    return;
                }
                setTimeout(poll, 2500);
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [orderId, refresh]);

    return (
        <div className="max-w-xl mx-auto px-6 py-20 text-center" data-testid="payment-success-page">
            <div className="surface p-10">
                {status === "checking" && (
                    <>
                        <Loader2 className="w-10 h-10 mx-auto mb-5 animate-spin text-accent" />
                        <h1 className="font-heading text-2xl font-semibold tracking-tight mb-2" data-testid="payment-checking">Confirming payment</h1>
                        <p className="text-zinc-500 font-mono text-xs">This usually takes a few seconds.</p>
                    </>
                )}
                {status === "paid" && (
                    <>
                        <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-accent/15 border border-accent/40 flex items-center justify-center">
                            <CheckCircle2 className="w-7 h-7 text-accent" strokeWidth={2} />
                        </div>
                        <h1 className="font-heading text-3xl font-bold tracking-tight mb-2" data-testid="payment-success">Payment received</h1>
                        <p className="text-zinc-400 mb-8">
                            <span className="font-mono text-accent font-bold">{details?.credits}</span> credits added to your account.
                        </p>
                        <div className="flex justify-center gap-3">
                            <button onClick={() => navigate("/generate")} className="btn-primary" data-testid="success-generate">Generate now</button>
                            <Link to="/dashboard" className="btn-ghost" data-testid="success-dashboard">Dashboard</Link>
                        </div>
                    </>
                )}
                {(status === "pending" || status === "failed" || status === "no-order") && (
                    <>
                        <XCircle className="w-12 h-12 mx-auto mb-4 text-zinc-500" />
                        <h1 className="font-heading text-2xl font-semibold tracking-tight mb-2" data-testid="payment-failed">
                            {status === "no-order" ? "No order to track" : "Couldn't confirm payment"}
                        </h1>
                        <p className="text-zinc-500 mb-6">If you were charged, contact support. Otherwise try again.</p>
                        <Link to="/pricing" className="btn-primary inline-flex">Back to pricing</Link>
                    </>
                )}
            </div>
        </div>
    );
}
